import React from "react";
import Autocomplete, { AutocompleteOption } from "../Autocomplete";
import { useFormContext } from "./FormContext";

interface FormAutocompleteProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  options: AutocompleteOption[];
  loading?: boolean;
  noOptionsText?: string;
  onAutocompleteChange?: (value: AutocompleteOption | null) => void;
  onInputChange?: (value: string) => void;
}

const FormAutocomplete: React.FC<FormAutocompleteProps> = ({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  fullWidth = true,
  options,
  loading = false,
  noOptionsText,
  onAutocompleteChange,
  onInputChange,
}) => {
  const { formData, validationResult, updateField, touchField, isSubmitted } =
    useFormContext();

  const value = formData[name];
  const selectedOption = options.find((opt) => opt.value === value) || null;

  const hasError = isSubmitted && validationResult.hasFieldError(name);
  const errorMessage = hasError ? validationResult.errors[name] : undefined;

  const handleChange = (newValue: AutocompleteOption | null) => {
    updateField(name, newValue?.value || null);
    touchField(name);
    onAutocompleteChange?.(newValue);
  };

  return (
    <Autocomplete
      name={name}
      label={label}
      placeholder={placeholder}
      value={selectedOption}
      onChange={handleChange}
      onInputChange={onInputChange}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      loading={loading}
      noOptionsText={noOptionsText}
      error={hasError}
      helperText={errorMessage}
    />
  );
};

export default FormAutocomplete;
